import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sprout, Wand2 } from "lucide-react";
import api from "../../api";
import { COUNTRIES } from "./EmployeeFormDialog";

const GENDERS = [
  { v: "", l: "—" }, { v: "female", l: "Femmina" }, { v: "male", l: "Maschio" },
  { v: "other", l: "Altro" }, { v: "not_reported", l: "Non dichiara" },
];
const cell = "border border-gray-200 text-[12.5px] px-1.5 py-1 outline-none focus:border-[#16345c] w-full bg-white";

export const EsgBulkDataDialog = ({ open, onClose, employees, ccnls = [], onSaved }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const ccnlMap = useMemo(() => Object.fromEntries(ccnls.map((c) => [c.id, c])), [ccnls]);

  useEffect(() => {
    if (open) {
      setRows(employees.map((e) => ({
        id: e.id, name: e.name, matricola: e.matricola,
        gender: e.gender || "", country: e.country || "IT",
        part_time_pct: e.part_time_pct ?? 100,
        weekly_hours_contract: e.weekly_hours_contract ?? "",
        annual_gross: e.annual_gross ?? "",
        ccnl_id: e.ccnl_id || "",
      })));
    }
  }, [open, employees]);

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const fillFteMissing = () => {
    setRows((rs) => rs.map((r) => (r.part_time_pct == null || r.part_time_pct === "" ? { ...r, part_time_pct: 100 } : r)));
    toast.success("FTE mancanti impostati a 100%");
  };

  const fillHoursFromCcnl = () => {
    setRows((rs) => rs.map((r) => {
      if (r.weekly_hours_contract !== "" && r.weekly_hours_contract != null) return r;
      const c = ccnlMap[r.ccnl_id];
      if (!c) return r;
      const pct = Number(r.part_time_pct) > 0 ? Number(r.part_time_pct) : 100;
      return { ...r, weekly_hours_contract: Math.round((c.weekly_hours || 40) * pct / 100 * 10) / 10 };
    }));
    toast.success("Ore contrattuali precompilate dai CCNL");
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = rows.map((r) => ({
        id: r.id,
        gender: r.gender || "",
        country: (r.country || "IT"),
        part_time_pct: Number(r.part_time_pct) || 100,
        weekly_hours_contract: (r.weekly_hours_contract === "" || r.weekly_hours_contract == null) ? null : Number(r.weekly_hours_contract),
        annual_gross: (r.annual_gross === "" || r.annual_gross == null) ? null : Number(r.annual_gross),
      }));
      const res = await api.post("/employees/esg-bulk", { items });
      toast.success(`Dati aggiornati: ${res.data?.updated ?? 0} dipendenti`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[860px] max-h-[90vh] overflow-y-auto" data-testid="esg-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sprout size={18} className="text-[#009640]" /> Completamento dati ESG</DialogTitle>
          <DialogDescription className="text-[13px] text-gray-600">
            Imposta genere, Paese, % part-time e ore settimanali contrattuali per tutti i dipendenti in un'unica schermata. Questi campi sbloccano i domini ESG "Parziale" / "0%".
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mb-2">
          <button type="button" onClick={fillFteMissing} data-testid="esg-bulk-fill-fte" className="flex items-center gap-1.5 text-[12px] font-bold text-[#0d6cb8] border border-[#0d6cb8] hover:bg-blue-50 px-3 py-1.5 rounded-sm transition-colors"><Wand2 size={14} /> Completa FTE mancanti al 100%</button>
          <button type="button" onClick={fillHoursFromCcnl} data-testid="esg-bulk-fill-hours" className="flex items-center gap-1.5 text-[12px] font-bold text-[#0d6cb8] border border-[#0d6cb8] hover:bg-blue-50 px-3 py-1.5 rounded-sm transition-colors"><Wand2 size={14} /> Ore contrattuali da CCNL</button>
        </div>

        <div className="border border-gray-200 rounded-sm overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr className="bg-[#16345c] text-white text-[11px] uppercase tracking-wide">
                <th className="text-left px-2 py-2 font-bold">Dipendente</th>
                <th className="text-left px-2 py-2 font-bold w-[130px]">Genere</th>
                <th className="text-left px-2 py-2 font-bold w-[140px]">Paese</th>
                <th className="text-left px-2 py-2 font-bold w-[80px]">% PT</th>
                <th className="text-left px-2 py-2 font-bold w-[100px]">Ore/sett.</th>
                <th className="text-left px-2 py-2 font-bold w-[110px]">RAL €</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`esg-bulk-row-${r.id}`} className="odd:bg-white even:bg-gray-50">
                  <td className="px-2 py-1 text-[12.5px]">
                    <div className="font-semibold text-[#16345c] leading-tight">{r.name}</div>
                    <div className="text-[11px] text-gray-400">{r.matricola}</div>
                  </td>
                  <td className="px-1 py-1">
                    <select value={r.gender} data-testid={`esg-bulk-gender-${r.id}`} onChange={(e) => setRow(r.id, { gender: e.target.value })} className={cell}>
                      {GENDERS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-1">
                    <select value={r.country} data-testid={`esg-bulk-country-${r.id}`} onChange={(e) => setRow(r.id, { country: e.target.value })} className={cell}>
                      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" min="1" max="100" value={r.part_time_pct} data-testid={`esg-bulk-pt-${r.id}`} onChange={(e) => setRow(r.id, { part_time_pct: e.target.value })} className={cell} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" min="0" step="0.5" value={r.weekly_hours_contract} placeholder="auto" data-testid={`esg-bulk-hours-${r.id}`} onChange={(e) => setRow(r.id, { weekly_hours_contract: e.target.value })} className={cell} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" min="0" step="500" value={r.annual_gross} placeholder="—" data-testid={`esg-bulk-ral-${r.id}`} onChange={(e) => setRow(r.id, { annual_gross: e.target.value })} className={cell} />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400 text-[13px]">Nessun dipendente da completare.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
          <button onClick={save} disabled={saving || rows.length === 0} data-testid="esg-bulk-save" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-60">{saving ? "Salvataggio..." : "Salva dati ESG"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
